// The city guide: places to see, restaurants and things to do in a city, each a link to Google Maps or OpenStreetMap (the
// visitor's pick, MAPS). A city's guide is site/guide/<id>.json, written by hand and checked by guideProblems
// (tests/guide.test.mjs, tools/seo/seo.mjs).
// tools/seo/seo.mjs writes a city page's own guide into its HTML, for crawlers; the app (site/js/app.js, "city guide")
// fetches any other city's when the guide opens. Both render it with guideHtml, so they match.

/** The cities that have a guide, in registry order; the others show no pill. Written by tools/seo/seo.mjs from site/guide/. */
// seo:guides
export const GUIDES = [
  'paris', 'tokyo', 'new-york', 'london', 'rio', 'istanbul', 'hong-kong', 'sydney', 'san-francisco', 'hamburg',
  'amsterdam', 'dubai', 'madrid', 'rome', 'prague', 'munich', 'vancouver', 'seoul', 'bangkok', 'cape-town',
  'singapore', 'mexico-city', 'new-delhi', 'cairo', 'lisbon', 'osaka', 'berlin', 'athens', 'copenhagen',
  'seattle', 'florence', 'dublin', 'miami', 'budapest', 'kyoto', 'venice', 'marrakech', 'buenos-aires',
  'chicago', 'barcelona', 'stockholm', 'taipei', 'chongqing', 'melbourne', 'new-orleans', 'toronto', 'boston',
  'los-angeles', 'bali', 'las-vegas', 'honolulu', 'agra', 'kuala-lumpur', 'reykjavik', 'havana', 'porto',
  'warsaw', 'manila', 'portland', 'valletta', 'almaty', 'helsinki', 'ho-chi-minh-city', 'auckland', 'cartagena',
  'nairobi', 'winnipeg',
];
// /seo:guides

/** The lists, in tab order: `id` is the guide's key, `tab` the tab's label, `heading` the list's heading for the city. */
export const GUIDE_LISTS = [
  { id: 'places', tab: 'Places', heading: city => `Places to see in ${city}` },
  { id: 'restaurants', tab: 'Restaurants', heading: city => `Where to eat in ${city}` },
  { id: 'todo', tab: 'Things to do', heading: city => `Things to do in ${city}` },
];
export const GUIDE_MIN = 4, GUIDE_MAX = 6;           // entries a list
export const NAME_MAX = 48, AREA_MAX = 32, TEXT_MIN = 50, TEXT_MAX = 170, Q_MAX = 100;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const isDay = s => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(new Date(s)) && new Date(s).toISOString().slice(0, 10) === s;
const oneLine = (s, max, min = 1) => typeof s === 'string' && s.length >= min && s.length <= max && /^\S(.*\S)?$/.test(s) && !/[\n\r\t]/.test(s);

/**
 * Where a place opens, the switch in the guide's head: `id` is the visitor's pick as kept ('maps' in the app's store),
 * `name` its label, `url` its search for a query. Google Maps' is the Maps URLs API, which opens the Maps app on phones;
 * the first is the default, and a city page's own guide (written for crawlers) links to it.
 */
export const MAPS = [
  { id: 'google', name: 'Google Maps', url: query => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` },
  { id: 'osm', name: 'OpenStreetMap', url: query => `https://www.openstreetmap.org/search?query=${encodeURIComponent(query)}` },
];
const mapsOf = id => MAPS.find(m => m.id === id) ?? MAPS[0];

/**
 * What a place's link searches for on the maps picked: its `q` (or else its name), in the city and its country. On
 * OpenStreetMap, its `osm` instead when it has one, whole: OSM's search knows a place by its OSM name and finds nothing
 * for a place outside the city with the city in the query (tools/seo/guide-maps.mjs finds the ones that need it).
 */
export const placeQuery = (entry, city, maps) =>
  (mapsOf(maps).id === 'osm' && entry.osm) || `${entry.q ?? entry.name}, ${city.name}, ${city.country}`;

/** The search for an entry in the city, on the maps picked (Google Maps unless it's a MAPS id). */
export const mapsUrl = (entry, city, maps) => mapsOf(maps).url(placeQuery(entry, city, maps));

/** A place's link on the maps picked: where it goes, and what a screen reader hears. The app relinks with it on a switch. */
export const placeLink = (name, query, maps) =>
  ({ href: mapsOf(maps).url(query), label: `${name} on ${mapsOf(maps).name} (opens in a new tab)` });

/** What's wrong with a city's guide, a line each; none when it's fine. */
export function guideProblems(guide) {
  if (!guide || typeof guide !== 'object' || Array.isArray(guide)) return ['the guide should be an object'];
  const problems = [];
  const keys = ['checked', ...GUIDE_LISTS.map(l => l.id)];
  for (const k of Object.keys(guide)) if (!keys.includes(k)) problems.push(`unknown key "${k}" (the keys are ${keys.join(', ')})`);
  if (!isDay(guide.checked)) problems.push('checked should be the day the entries were checked, as YYYY-MM-DD');
  const names = new Set();
  for (const { id } of GUIDE_LISTS) {
    const list = guide[id];
    if (!Array.isArray(list) || list.length < GUIDE_MIN || list.length > GUIDE_MAX) {
      problems.push(`${id} should list ${GUIDE_MIN} to ${GUIDE_MAX} entries`);
      continue;
    }
    list.forEach((entry, i) => {
      const at = `${id}[${i}]`;
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) { problems.push(`${at} should be { name, area, text, q?, osm? }`); return; }
      for (const k of Object.keys(entry)) if (!['name', 'area', 'text', 'q', 'osm'].includes(k)) problems.push(`${at}: unknown key "${k}"`);
      if (!oneLine(entry.name, NAME_MAX)) problems.push(`${at}.name should be one line of up to ${NAME_MAX} characters`);
      if (!oneLine(entry.area, AREA_MAX)) problems.push(`${at}.area should be one line of up to ${AREA_MAX} characters`);
      if (!oneLine(entry.text, TEXT_MAX, TEXT_MIN) || !/[.!?]$/.test(entry.text ?? ''))
        problems.push(`${at}.text should be a sentence or two, ${TEXT_MIN} to ${TEXT_MAX} characters, ending in a full stop`);
      if (entry.q !== undefined && !oneLine(entry.q, Q_MAX)) problems.push(`${at}.q should be one line of up to ${Q_MAX} characters`);
      if (entry.osm !== undefined && !oneLine(entry.osm, Q_MAX)) problems.push(`${at}.osm should be one line of up to ${Q_MAX} characters`);
      const name = String(entry.name).toLowerCase();
      if (names.has(name)) problems.push(`${at}: "${entry.name}" is in the guide twice`);
      names.add(name);
    });
  }
  return problems;
}

/** The checked day as a month: "October 2026". */
const checkedMonth = day => `${MONTHS[Number(day.slice(5, 7)) - 1]} ${day.slice(0, 4)}`;

/**
 * The guide's panel, the dialog's .guide-content: a tab per list and the lists under them, in the about panel's markup
 * (site/css/app.css styles both). Without the app the lists simply stack; the app makes them tabs. `city` is
 * { name, country }: the name on the city's title and the registry's country. Each place links to its search on `maps`
 * (a MAPS id; Google Maps unless it's one) and carries its query on each (data-google, data-osm), so the switch can
 * relink it in place.
 */
export function guideHtml(city, guide, maps) {
  const name = esc(city.name), on = mapsOf(maps).id;
  return [
    `<div class="about-tabs" role="tablist" aria-label="${name} city guide">`,
    ...GUIDE_LISTS.map(({ id, tab }, i) =>
      `<button type="button" role="tab" id="guide-tab-${id}" aria-controls="guide-${id}" aria-selected="${i === 0}"${i ? ' tabindex="-1"' : ''}>${tab}</button>`),
    '</div>',
    '<div class="about-body">',
    `<h2 id="guideTitle">${name} city guide</h2>`,
    `<div class="guide-meta"><p>Picked by Lofi Cities in ${checkedMonth(guide.checked)}.</p>`,
    `<div class="guide-pick">Places open in <div class="view-switch guide-maps" role="group" aria-label="Open places in">${MAPS.map(m =>
      `<button type="button" data-maps="${m.id}" aria-pressed="${m.id === on}">${m.name}</button>`).join('')}</div></div></div>`,
    ...GUIDE_LISTS.flatMap(({ id, heading }) => [
      `<section class="about-panel" id="guide-${id}" role="tabpanel" aria-labelledby="guide-tab-${id}">`,
      `<h3>${esc(heading(city.name))}</h3>`,
      `<ul class="guide-list" data-list="${id}">`,
      ...guide[id].map(entry => {
        const link = placeLink(entry.name, placeQuery(entry, city, on), on);
        const queries = MAPS.map(m => ` data-${m.id}="${esc(placeQuery(entry, city, m.id))}"`).join('');
        return `<li><a href="${esc(link.href)}"${queries} target="_blank" rel="noopener" aria-label="${esc(link.label)}">${esc(entry.name)}<span aria-hidden="true"> ↗</span></a> <span class="guide-area">${esc(entry.area)}</span><p>${esc(entry.text)}</p></li>`;
      }),
      '</ul>',
      '</section>',
    ]),
    '</div>',
  ].join('\n');
}
